import {
    EventAccess,
    EventMineRead,
    EventParticipation,
    EventRead,
    EventState,
    ResponseError,
} from "@openapi";
import type { TFunction } from "i18next";

// How often event mode re-reads GET /judge/events/{id}/me. The countdown is
// re-synced to the server on every poll, so it never drifts more than this.
export const EVENT_POLL_INTERVAL_MS = 15000;

// The API refuses organization features with this detail while the
// organization has no valid license (blocks-code/management#139).
export const LICENSE_REQUIRED_DETAIL = "Organization license required";

// Backend error details from the event endpoints, mapped to translation keys
// so students see a readable message in their own language.
const EVENT_ERROR_KEYS: Record<string, string> = {
    "Event has not started": "events.error.eventNotStarted",
    "Organization has not started": "events.error.organizationNotStarted",
    "Deadline has passed": "events.error.deadlinePassed",
    "Problem is not part of this event": "events.error.problemNotInEvent",
    "You are not a competitor in this event": "events.error.notCompetitor",
    "Problem has not been initialized": "events.error.problemNotInitialized",
    // blocks-code/management#149: practice submits on a running event's problem
    "Problem is reserved for an event that has not closed":
        "events.error.problemReserved",
    [LICENSE_REQUIRED_DETAIL]: "events.error.licenseRequired",
    "Not authorized to submit this code": "events.error.notYourCode",
    "Code does not have a problem associated":
        "events.error.codeWithoutProblem",
    // blocks-code/management#247: an empty or whitespace-only program
    "Code is empty": "events.error.codeEmpty",
    // blocks-code/studio#251: questions to the organizers of an event
    "Questions can be asked only while the event is open":
        "events.questions.closed",
    "Too many questions. Please try again later.": "events.questions.tooMany",
    "Only a competitor or an admin of a listed organization can ask questions":
        "events.questions.notAllowed",
    "An answered question can no longer be deleted":
        "events.questions.answeredNotDeletable",
    "Team is full": "events.error.teamFull",
    "No team with this code": "events.error.noTeamWithCode",
    "User already competes in this event": "events.error.alreadyCompeting",
    "Teams are frozen while the event runs": "events.error.teamsFrozen",
    "Teams with join codes exist only in public user events":
        "events.error.notPublicUserEvent",
    "You are not in a team of this event": "events.error.notInTeam",
    "A competitor with this name already exists in this event":
        "events.error.teamNameTaken",
    "Could not generate a join code; try again": "events.error.joinCodeRetry",
    // blocks-code/management#143: too many unknown join codes in an hour or a day
    "Too many join attempts. Please try again later.":
        "events.error.tooManyJoinAttempts",
    // blocks-code/studio#230: flagging a test case result
    "Submission result not found": "submissionResult.flag.notFound",
    "Only the submission's author may flag its results":
        "submissionResult.flag.notAuthor",
};

type EventWindow = Pick<
    EventRead,
    "participation" | "access" | "state" | "available_start"
>;

// Public user events let anyone form or join a team with a code, until the
// window ends (late joining is allowed).
export const isOpenUserEvent = (event: EventWindow): boolean =>
    event.participation === EventParticipation.Users &&
    event.access === EventAccess.Public &&
    event.state !== EventState.Closed;

// A solo event has teams of one: everyone competes alone, so there is no team
// to name or join with a code. A missing size counts as 1, like the picker.
export const isSoloEvent = (event: Pick<EventRead, "max_team_size">): boolean =>
    (event.max_team_size ?? 1) === 1;

// A user-event team can only be left before the window opens; the server has
// the final say and answers 409 afterwards.
export const canLeaveTeam = (
    event: Pick<EventMineRead, "organization_id" | "available_start">,
    now: number = Date.now(),
): boolean =>
    event.organization_id == null && event.available_start.getTime() > now;

export const errorStatus = (error: unknown): number | undefined =>
    error instanceof ResponseError ? error.response.status : undefined;

// Seconds from the Retry-After header of a failed generated-client call, or
// undefined when it is missing or not a number of seconds.
export const retryAfterSeconds = (error: unknown): number | undefined => {
    if (!(error instanceof ResponseError)) return undefined;
    const header = error.response.headers.get("Retry-After")?.trim();
    if (!header || !/^\d+$/.test(header)) return undefined;
    return Number(header);
};

// Reads the FastAPI `detail` from a failed generated-client call.
async function errorDetail(error: ResponseError): Promise<unknown> {
    try {
        const body = (await error.response.clone().json()) as {
            detail?: unknown;
        };
        return body.detail;
    } catch {
        return undefined;
    }
}

export async function isLicenseRequired(error: unknown): Promise<boolean> {
    return (
        error instanceof ResponseError &&
        error.response.status === 403 &&
        (await errorDetail(error)) === LICENSE_REQUIRED_DETAIL
    );
}

// Returns a translated message for a failed generated-client call when its
// detail is a known event error.
export async function eventErrorMessage(
    error: unknown,
    t: TFunction,
): Promise<string> {
    if (error instanceof ResponseError) {
        const detail = await errorDetail(error);
        if (typeof detail === "string") {
            const key = EVENT_ERROR_KEYS[detail];
            return key ? t(key) : detail;
        }
        if (detail && typeof detail === "object" && "message" in detail) {
            return String((detail as { message: unknown }).message);
        }
        return t("events.error.generic", { status: error.response.status });
    }
    if (error instanceof Error && error.message) {
        return error.message;
    }
    return t("message.unknownError");
}

// Formats a number of seconds as H:MM:SS (or MM:SS under an hour).
export function formatCountdown(totalSeconds: number): string {
    const seconds = Math.max(0, Math.floor(totalSeconds));
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    const mm = String(m).padStart(2, "0");
    const ss = String(s).padStart(2, "0");
    return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

// Formats an event date in the app's locale (EN/HR), not the browser's.
export function formatEventDate(date: Date, locale: string): string {
    return date.toLocaleString(locale === "hr" ? "hr-HR" : "en-GB", {
        dateStyle: "medium",
        timeStyle: "short",
    });
}
